/**
 * GoogleConnectButton Component
 * 
 * This component manages Google OAuth 2.0 authentication flow, providing a customizable button
 * that initiates the authentication process in a popup window and handles the resulting
 * authorization code.
 * 
 * Features:
 * - OAuth 2.0 authentication flow with Google
 * - Popup-based authentication UI
 * - State parameter for CSRF protection
 * - Support for custom scopes
 * - Configurable client ID via props
 * - Integration with Redux for state management
 */
/* Aniket Bhesaniya */
import React, { useEffect, useCallback } from 'react'
import { connect } from 'react-redux'
import { setProps, clearDetailsModalData } from '../../redux/actions'
import { store } from '../../index'
import { useNavigate } from 'react-router-dom'
import { Config, IISMethods } from '../../config/IISMethods'

// Constants for Google OAuth configuration
const OAUTH_ENDPOINTS = {
  AUTHORIZE: 'https://accounts.google.com/o/oauth2/v2/auth'
}

// Default scopes for basic profile access
const DEFAULT_SCOPES = [
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile'
]

const GoogleAuthentication = (props) => {
  // Access Redux store state
  const getCurrentState = () => store.getState()

  // Hook for programmatic navigation
  const navigate = useNavigate()

  // OAuth configuration from props or defaults
  const CLIENT_ID = props.clientId
  const SCOPE = props?.scope?.join(' ') || DEFAULT_SCOPES.join(' ')
  const REDIRECT_URI = Config.googleredirectionurl

  /**
   * Process the authorization code received from Google OAuth
   * Updates Redux store with the code and navigates to the configured destination
   * 
   * @param {string} code - The authorization code from Google
   * @param {string} returnUrl - The URL to return to after processing
   */
  const processAuthCode = useCallback(async (code, returnUrl) => {
    try {
      // Handle different component use cases based on ismeeting prop
      if (props?.ismeeting) {
        // Clear any existing modal data
        await props.clearDetailsModalData()

        // Set the authorization code in modal data
        await props.setProps({
          detailModalData: {
            code: code,
          },
        })
      } else {
        // Set the authorization code in form data
        await props.setProps({
          formData: {
            ...getCurrentState().formData,
            code: code,
          },
        })
      }

      // Execute additional callback if provided by parent component
      if (props.handleAddButtonClick) {
        await props.handleAddButtonClick()
      }

      // Determine where to navigate after processing

      // Clean up stored return URL
      IISMethods.clearLocalData('oauth_return_url')

      // Navigate to integration page based on config
      navigate(`/${Config.pagenamelist['integrationpagename']}`)
    } catch (error) {
      console.error('Error processing Google authentication code:', error)
    }
  }, [props, navigate, getCurrentState])

  // Set up event listeners and handle redirect processing
  useEffect(() => {
    /**
     * Handles messages from the authentication popup window
     * Verifies and processes authorization codes
     * 
     * @param {MessageEvent} event - The message event containing auth data
     */
    const handleAuthMessage = (event) => {
      // TODO: In production, uncomment this line to verify origin for security
      // if (event.origin !== 'https://integration.bizcompass.app') return

      const { code, returnUrl, platform } = event.data || {}
      // Only process if this is from Google
      if (platform !== 'Google') return
      // Only process if code is present
      if (code) {
        processAuthCode(code, returnUrl)
      }
    }

    // Add event listener for popup communication
    window.addEventListener('message', handleAuthMessage)

    /**
     * Processes OAuth redirect if we're on the callback page
     * Called automatically if the current page is the OAuth callback URL
     */
    const checkPopupRedirectResult = () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const error = params.get('error')
      const state = params.get('state')

      if (error) {
        document.body.innerHTML = `
      <div style="text-align:center; margin-top:100px; font-family:Arial,sans-serif">
        <h2>Authentication failed or was cancelled</h2>
        <p>You can close this window and try again.</p>
      </div>
    `
        setTimeout(() => window.close(), 2000)
        return
      }

      if (code && window.opener) {
        try {
          let returnUrl = ''
          if (state) {
            const stateObj = JSON.parse(decodeURIComponent(atob(state)))
            returnUrl = stateObj.returnUrl
          }

          window.opener.postMessage({ code, returnUrl, platform: 'Google' }, '*')
          document.body.innerHTML = `
        <div style="text-align:center; margin-top:100px; font-family:Arial,sans-serif">
          <h2>Authentication successful!</h2>
          <p>You can close this window now.</p>
        </div>
      `
          setTimeout(() => window.close(), 2000)
        } catch (error) {
          console.error('Error processing Google auth redirect:', error)
        }
      }
    }


    // Check if current page is the callback page and process if needed
    if (window.location.pathname.includes('/auth/callback')) {
      checkPopupRedirectResult()
    }

    // Clean up event listener on component unmount
    return () => window.removeEventListener('message', handleAuthMessage)
  }, [processAuthCode])

  /**
   * Initiates the Google OAuth login process
   * Opens a popup window with the authorization URL
   */
  const initiateLogin = () => {
    try {
      // Store current URL to return to after authentication
      const currentUrl = window.location.href
      IISMethods.setLocalData('oauth_return_url', currentUrl)
      const userId = IISMethods.getCookiesData("loginInfo")?.uid
      IISMethods.setSessionData('oauth_nonce', userId)
      // Create state parameter with return URL and timestamp
      // This helps prevent CSRF attacks and enables return navigation
      const stateObj = {
        returnUrl: currentUrl,
        timestamp: Date.now(),
        platform: 'Google',
        nonce : userId
      }

      // Encode state as base64 string
      const state = btoa(encodeURIComponent(JSON.stringify(stateObj)))

      // Create authorization URL with all required parameters
      const authUrl = new URL(OAUTH_ENDPOINTS.AUTHORIZE)

      // Add all required OAuth parameters
      authUrl.searchParams.append('client_id', CLIENT_ID)
      authUrl.searchParams.append('redirect_uri', REDIRECT_URI)
      authUrl.searchParams.append('response_type', 'code')
      authUrl.searchParams.append('scope', SCOPE)
      authUrl.searchParams.append('access_type', 'offline')  // Get refresh token
      authUrl.searchParams.append('state', state)
      authUrl.searchParams.append('prompt', 'consent')  // Always show consent screen
      authUrl.searchParams.append('include_granted_scopes', 'true')  // Include previously granted scopes

      // Open popup window for authentication
      window.open(authUrl.toString(), 'google-oauth')
    } catch (error) {
      console.error('Failed to initiate Google login:', error)
    }
  }

  // Clone child element and attach click handler
  // This makes the component flexible as it can wrap any element
  return React.cloneElement(props.children, {
    onClick: (e) => {
      e.preventDefault()
      initiateLogin()
    },
  })
}

// Redux connection configuration
const mapStateToProps = () => ({})

const mapDispatchToProps = (dispatch) => ({
  setProps: (payload) => dispatch(setProps(payload)),
  clearDetailsModalData: (payload) => dispatch(clearDetailsModalData(payload)),
})

export default connect(mapStateToProps, mapDispatchToProps)(GoogleAuthentication)